import React from 'react';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ThemeSwitcher from '../common/ThemeSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import BrandLockup from '../brand/BrandLockup';

interface AuthPageShellProps {
  title: string;
  description?: string;
  icon: React.ReactNode;
  backTo?: string;
  backLabel?: string;
  children: React.ReactNode;
  wide?: boolean;
}

/** Shared public-page frame. Forms stay inside their own route components. */
const AuthPageShell: React.FC<AuthPageShellProps> = ({ title, description, icon, backTo = '/', backLabel, children, wide = false }) => {
  const { t } = useLanguage();

  return (
    <Box component="main" sx={{ minHeight: '100vh', py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center' }}>
      <Container maxWidth={wide ? 'lg' : 'md'} disableGutters>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, px: { xs: 1, md: 0 } }}>
          <Button component={Link} to={backTo} startIcon={<ArrowBack />} sx={{ color: 'text.secondary' }}>
            {backLabel || t('auth.backToHome')}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}><LanguageSwitcher /><ThemeSwitcher /></Box>
        </Box>
        <Paper variant="outlined" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: wide ? 'minmax(250px, .68fr) minmax(0, 1.32fr)' : 'minmax(250px, .85fr) minmax(0, 1.15fr)' }, overflow: 'hidden', borderRadius: 3, boxShadow: theme => `0 20px 55px -40px ${theme.palette.text.primary}` }}>
          <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: { xs: 150, md: 550 }, p: { xs: 2.5, md: 4 }, color: 'primary.contrastText', background: theme => `linear-gradient(145deg, ${theme.palette.primary.dark || theme.palette.primary.main}, ${theme.palette.primary.main})` }}>
            <Box sx={{ position: 'absolute', width: 290, height: 290, border: '1px solid rgba(255,255,255,.18)', borderRadius: '50%', right: -110, top: -115 }} />
            <Box sx={{ position: 'absolute', width: 220, height: 220, border: '1px solid rgba(255,255,255,.15)', borderRadius: '50%', right: -80, top: -80 }} />
            <Box sx={{ position: 'absolute', width: 170, height: 170, border: '1px solid rgba(255,255,255,.12)', borderRadius: '50%', left: -100, bottom: -80 }} />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <BrandLockup markSize={48} fontSize={26} onDarkSurface gap={1} />
              <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 'auto', mb: 2 }}>
                <Typography variant="h4" component="p" sx={{ maxWidth: 330, fontSize: 30, color: 'inherit', lineHeight: 1.2 }}>{t('app.description')}</Typography>
                <Box sx={{ width: 48, height: 3, borderRadius: 4, bgcolor: 'rgba(255,255,255,.65)', mt: 3 }} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ p: { xs: 2.5, sm: 4, md: wide ? 4 : 5 }, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{ width: 42, height: 42, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main', flexShrink: 0 }}>{icon}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="h1" variant="h5" fontWeight={700}>{title}</Typography>
                {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
              </Box>
            </Box>
            {children}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AuthPageShell;
